// The browser's own storage. Every read and write is guarded: storage can
// be missing or refused (a private window, a full disk), and the game must
// still play. Nothing here is ever sent anywhere; only the visit count on
// ternion.one (src/main/count.ts) talks to a server, and it sends no save.

import { BEEN_KEY, SAVE_KEY, V1_SAVE_KEY, V2_SAVE_KEY } from "../boundary/keys.ts"
import type { Stored } from "../boundary/save.ts"

export type Storage = {
  readonly read: () => Stored
  readonly write: (text: string) => void
  // which screen this tab was on, so a reload comes back to it
  readonly readScreen: () => string | undefined
  readonly writeScreen: (screen: string) => void
}

const SCREEN_KEY = "ternion.screen"

const guarded = <T>(f: () => T, fallback: T): T => {
  try {
    return f()
  } catch {
    return fallback
  }
}

export const browserStorage = (): Storage => ({
  read: () => ({
    v3: guarded(() => localStorage.getItem(SAVE_KEY) ?? undefined, undefined),
    v2: guarded(() => localStorage.getItem(V2_SAVE_KEY) ?? undefined, undefined),
    v1: guarded(() => localStorage.getItem(V1_SAVE_KEY) ?? undefined, undefined),
  }),
  write: (text) => guarded(() => localStorage.setItem(SAVE_KEY, text), undefined),
  readScreen: () => guarded(() => sessionStorage.getItem(SCREEN_KEY) ?? undefined, undefined),
  writeScreen: (screen) => guarded(() => sessionStorage.setItem(SCREEN_KEY, screen), undefined),
})

// Whether this browser has been here before: it saw the introit, or it
// holds a save from any version. If storage cannot be read, say yes, so
// nobody is sent to the introit on every visit.
export const beenHere = (): boolean =>
  guarded(() => [BEEN_KEY, SAVE_KEY, V2_SAVE_KEY, V1_SAVE_KEY].some((k) => localStorage.getItem(k) !== null), true)

export const markBeenHere = (): void => guarded(() => localStorage.setItem(BEEN_KEY, "1"), undefined)

export type MemoryStorage = Storage & { readonly saved: () => string | undefined }

// Storage that lives only as long as the page: for tests.
export const memoryStorage = (start: Stored): MemoryStorage => {
  let current = start.v3
  let screen: string | undefined
  return {
    read: () => ({ ...start, v3: current }),
    write: (text) => {
      current = text
    },
    readScreen: () => screen,
    writeScreen: (s) => {
      screen = s
    },
    saved: () => current,
  }
}
